import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AlertTriangle, LogOut, Loader2 } from "lucide-react";

interface Props {
  networkId: string;
  shipperName: string;
  activeDispatchCount: number;
  onLeft: () => void;
  trigger?: React.ReactNode;
}

/**
 * Broker-facing "leave this shipper's network" confirmation. Calls the
 * `leave_shipper_network` SECURITY DEFINER RPC — brokers can't UPDATE
 * shipper-owned membership rows directly (RLS restricts UPDATE to the shipper).
 *
 * The RPC guards against orphaning a live load. If it returns
 * `{ok:false, code:'active_dispatch'}`, we surface the count and refuse to
 * close.
 */
export function LeaveNetworkDialog({
  networkId,
  shipperName,
  activeDispatchCount,
  onLeft,
  trigger,
}: Props) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const hasActive = activeDispatchCount > 0;

  const confirm = async () => {
    setBusy(true);
    try {
      const { data, error } = await supabase.rpc(
        "leave_shipper_network" as any,
        { _network_id: networkId },
      );
      if (error) throw error;
      const result = data as { ok: boolean; code?: string; active_count?: number } | null;
      if (result && result.ok === false) {
        if (result.code === "active_dispatch") {
          toast.error(
            `You have ${result.active_count} active load${result.active_count === 1 ? "" : "s"} with this shipper. Complete or close them before leaving.`,
          );
        } else {
          toast.error("Unable to leave this network");
        }
        return;
      }
      toast.success(`Left ${shipperName}'s network`);
      setOpen(false);
      onLeft();
    } catch (e: any) {
      toast.error(e?.message || "Failed to leave network");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <span onClick={() => setOpen(true)} className="inline-flex">
        {trigger ?? (
          <Button
            size="sm"
            variant="ghost"
            className="h-8 gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <LogOut className="h-3.5 w-3.5" /> Leave
          </Button>
        )}
      </span>

      <AlertDialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave {shipperName}'s network?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  You'll stop seeing their private loads and won't be able to bid on their direct
                  posts. Your past bid and load history stays intact.
                </p>
                {hasActive && (
                  <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2.5 flex gap-2">
                    <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                    <div className="text-xs text-destructive">
                      <div className="font-semibold">
                        {activeDispatchCount} active load{activeDispatchCount === 1 ? "" : "s"} in
                        progress
                      </div>
                      <div>
                        Finish or close them before leaving — you can't walk away from a live
                        dispatch.
                      </div>
                    </div>
                  </div>
                )}
                <p className="text-muted-foreground">
                  You can request to rejoin later if they open access again.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Stay in network</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (!hasActive) confirm();
              }}
              disabled={busy || hasActive}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Leaving…
                </>
              ) : (
                <>
                  <LogOut className="h-4 w-4 mr-1.5" /> Leave network
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
